import type { ConfiguratorState } from './configurator-types';

/**
 * The immutable product baseline supplied by the customer. Design-only preview
 * fields live here together with the dimensions confirmed by the source TCN so
 * the form, 3D preview, payload and TCN release gate cannot drift independently.
 */
export const REFERENCE_CONFIG = {
  productId: 'door-handle-edge',
  machineId: 'bdv-5axis',
  materialId: 'wood',
  widthMm: 426,
  heightMm: 276,
  thicknessMm: 20,
  quantity: 1,
  handle: {
    openSide: 'right',
    lengthMm: 285,
    offsetMm: 70,
    faceWidthMm: 30,
    depthMm: 17.5,
    transitionLengthMm: 50,
    transitionRoundness: 78,
    grooveWidthMm: 12,
    roughingAllowanceMm: 0.5,
  },
  edgeTreatment: {
    profile: 'roundover',
    sizeMm: 2,
    cornerRadiusMm: 2,
  },
  stockAllowanceMm: {
    widthMm: 3,
    heightMm: 0,
    thicknessMm: 0,
  },
  finishId: 'oil',
  includeQuote: true,
  includeDrawing: true,
  includeConfigFile: true,
  includeTcnFile: true,
  customerName: '',
  customerEmail: '',
  company: '',
  phone: '',
  note: '',
  consent: false,
} as const satisfies ConfiguratorState;

export function createReferenceConfiguratorState(): ConfiguratorState {
  return {
    ...REFERENCE_CONFIG,
    handle: { ...REFERENCE_CONFIG.handle },
    edgeTreatment: { ...REFERENCE_CONFIG.edgeTreatment },
    stockAllowanceMm: { ...REFERENCE_CONFIG.stockAllowanceMm },
  };
}

export const REFERENCE_TCN_SOURCE = {
  templateId: 'dvierka-madlo-p-reference-v1',
  filename: 'dvierka madloP.tcn',
  dialectId: 'tpa-albatros-edicad-2.8.21',
  dialect: 'TPA/ALBATROS/EDICAD 2.8.21',
  encoding: 'UTF-16LE',
  lineEnding: 'CRLF',
  byteLength: 3_118,
  sha256: 'b3930ca512f992991dd262bcb5b8115368fb847528373f31bd77fcccffd3709a',
  toolId: 1009,
  macro: '..\\custom\\mcr\\fresatebarnesting.tmcr',
} as const;

/** Exact decoded contents of the supplied source file, excluding its UTF-16LE BOM. */
const REFERENCE_TCN_LINES = [
  'TPA\\ALBATROS\\EDICAD\\02.00:1564:r0w0h0s51',
  '::SIDE=0;1;',
  '::UNm DL=429 DH=276 DS=20',
  "'tcn version=2.8.21",
  "'code=unicode",
  'EXE{',
  '#0=0',
  '#1=0',
  '#2=0',
  '#3=0',
  '#4=0',
  '}EXE',
  'OFFS{',
  '#0=0.0|0',
  '#1=0.0|0',
  '#2=0.0|0',
  '}OFFS',
  'VARV{',
  '#0=0.0|0',
  '#1=0.0|0',
  '}VARV',
  'VAR{',
  '}VAR',
  'SPEC{',
  '}SPEC',
  'INFO{',
  '}INFO',
  'OPTI{',
  ':: OPTKIND=%;0 OPTROUTER=%;0 LSTCOD=%0%1%2',
  '}OPTI',
  'LINK{',
  '}LINK',
  'SIDE#0{',
  'W#1511{ ::WT2 WF=1  #8098=..\\custom\\mcr\\fresatebarnesting.tmcr }W',
  '}SIDE',
  'SIDE#1{',
  'W#89{ ::WTs WS=34  #8015=0 #1=x/6+1+100 #2=2*(hf/2)-(-30) #3=-17 #201=1 #203=1 #205=1009 #1001=100 #9502=0 #9503=0 #9505=0 #9506=0 #9504=0 #8101=0 #8096=0 #8095=0 #37=0 #40=0 #39=0 #46=0 #8135=0 #8136=0 #38=0 #8180=0 #8181=0 #8185=0 #8186=0 }W',
  'W#2129{ ::WTa  #8015=0 #8019=0 #8017=0 #8026=x/6+50+100 #8027=2*(hf/2)-10 #8029=x/3+50+201-6 #8030=2*(hf/2)-10 #42=0 #49=0 }W',
  'W#2129{ ::WTa  #8015=0 #8019=0 #8017=0 #8026=x+50 #8027=2*(hf/2)-10 #8029=x+50 #8030=2*(hf/2)-(-30) #42=0 #49=0 }W',
  'W#89{ ::WTs WS=36  #8015=0 #1=x/6-1+100 #2=2*(hf/2)-(-30) #3=-17.5 #201=1 #203=1 #205=1009 #1001=100 #9502=0 #9503=0 #9505=0 #9506=0 #9504=0 #8101=0 #8096=0 #8095=0 #37=0 #40=0 #39=0 #46=0 #8135=0 #8136=0 #38=0 #8180=0 #8181=0 #8185=0 #8186=0 }W',
  'W#2129{ ::WTa  #8015=0 #8019=0 #8017=0 #8026=x/6-1+50+100 #8027=2*(hf/2)-11 #8029=x/3+50+202-6 #8030=2*(hf/2)-11 #42=0 #49=0 }W',
  'W#2129{ ::WTa  #8015=0 #8019=0 #8017=0 #8026=x+50 #8027=2*(hf/2)-11 #8029=x/3+50+202+50+2-6 #8030=2*(hf/2)-(-30) #42=0 #49=0 }W',
  '}SIDE',
  'SIDE#3{',
  '::DX=0 XY=1',
  '}SIDE',
  'SIDE#4{',
  '::DX=0 XY=1',
  '}SIDE',
  'SIDE#5{',
  '::DX=0 XY=1',
  '}SIDE',
  'SIDE#6{',
  '::DX=0 XY=1',
  '}SIDE',
] as const;

export function buildReferenceTcnText() {
  return `${REFERENCE_TCN_LINES.join('\r\n')}\r\n`;
}
